import type { ColumnCatalogDef, GroupNode } from '@/types/column-catalog'

/**
 * Auto-generate a GroupNode tree from a flat ColumnCatalogDef array.
 *
 * Uses `category` as the first level under root.
 * The optional `group` field supports nested sub-levels via the `>` separator.
 * For example, `group: "Price & Performance > Change"` creates two nested
 * GroupNode entries under the category.
 *
 * Columns without a `group` are placed directly under their category node.
 */
export function buildColumnTree(columns: ColumnCatalogDef[]): {
  tree: Record<string, GroupNode>
  columnMap: Record<string, ColumnCatalogDef>
} {
  const categoryOrder: string[] = []
  const seenCategories = new Set<string>()
  const categoryDirectColumns = new Map<string, string[]>()

  // First pass: identify categories and direct columns
  for (const col of columns) {
    if (!seenCategories.has(col.category)) {
      seenCategories.add(col.category)
      categoryOrder.push(col.category)
      categoryDirectColumns.set(col.category, [])
    }
    if (!col.group) {
      categoryDirectColumns.get(col.category)!.push(col.key)
    }
  }

  // Build root and category nodes
  const tree: Record<string, GroupNode> = {
    root: {
      key: 'root',
      label: 'All Columns',
      parentKey: null,
      children: categoryOrder.map((cat) => `cat::${cat}`),
      columnKeys: [],
    },
  }

  for (const cat of categoryOrder) {
    const catKey = `cat::${cat}`
    tree[catKey] = {
      key: catKey,
      label: cat,
      parentKey: 'root',
      children: [],
      columnKeys: categoryDirectColumns.get(cat) ?? [],
    }
  }

  // Second pass: build nested group paths and assign columns
  for (const col of columns) {
    if (!col.group) continue

    const parts = col.group.split(' > ')
    let parentKey = `cat::${col.category}`

    for (const part of parts) {
      const groupKey = `${parentKey}::${part}`
      if (!tree[groupKey]) {
        tree[groupKey] = {
          key: groupKey,
          label: part,
          parentKey,
          children: [],
          columnKeys: [],
        }
        tree[parentKey].children.push(groupKey)
      }
      parentKey = groupKey
    }

    // Add column to the leaf group
    tree[parentKey].columnKeys.push(col.key)
  }

  // Third pass: populate `childOrder` — an interleaved render order mixing
  // child-folder keys and direct column keys, sorted by first-authored
  // position in the flat `columns` array. A folder's position is the MIN
  // position among all columns anywhere in its subtree, so a folder sorts
  // as early as its earliest-authored descendant column.
  const columnIndex = new Map<string, number>(columns.map((c, i) => [c.key, i]))
  const subtreeMinIndexCache = new Map<string, number>()
  const subtreeMinIndex = (nodeKey: string): number => {
    const cached = subtreeMinIndexCache.get(nodeKey)
    if (cached !== undefined) return cached
    // Seed the cache before recursing so a (theoretically impossible, but
    // defensive) cycle can't recurse forever.
    subtreeMinIndexCache.set(nodeKey, Infinity)
    const node = tree[nodeKey]
    let min = Infinity
    if (node) {
      for (const colKey of node.columnKeys) {
        const idx = columnIndex.get(colKey)
        if (idx != null && idx < min) min = idx
      }
      for (const childKey of node.children) {
        const childMin = subtreeMinIndex(childKey)
        if (childMin < min) min = childMin
      }
    }
    subtreeMinIndexCache.set(nodeKey, min)
    return min
  }

  for (const node of Object.values(tree)) {
    const entries: { key: string; idx: number }[] = []
    for (const childKey of node.children) {
      entries.push({ key: childKey, idx: subtreeMinIndex(childKey) })
    }
    for (const colKey of node.columnKeys) {
      entries.push({ key: colKey, idx: columnIndex.get(colKey) ?? Infinity })
    }
    // Array.prototype.sort is stable (ES2019+), so ties (which shouldn't
    // occur since every column has a unique flat-array index) preserve
    // insertion order above (folders before their tied columns).
    entries.sort((a, b) => a.idx - b.idx)
    node.childOrder = entries.map((e) => e.key)
  }

  const columnMap: Record<string, ColumnCatalogDef> = Object.fromEntries(
    columns.map((c) => [c.key, c]),
  )

  return { tree, columnMap }
}

/**
 * Prune a curated GroupNode tree (e.g. the scanner's `SCANNER_TREE`) down to the
 * columns matching `keep`, dropping any group that ends up with no columns and no
 * surviving children. Lets a consumer reuse the scanner's exact folder hierarchy
 * for a filtered picker (e.g. the heatmap's percent-only / numeric-only fields)
 * instead of auto-building a divergent taxonomy with `buildColumnTree`.
 */
export function pruneColumnTree(
  tree: Record<string, GroupNode>,
  keep: (columnKey: string) => boolean,
): Record<string, GroupNode> {
  const next: Record<string, GroupNode> = {}
  const visit = (nodeKey: string): boolean => {
    const node = tree[nodeKey]
    if (!node) return false
    const columnKeys = node.columnKeys.filter(keep)
    // Visit children first so `next` is populated for the survivors.
    const children = node.children.filter((childKey) => visit(childKey))
    if (nodeKey !== 'root' && columnKeys.length === 0 && children.length === 0) return false
    const childOrder = node.childOrder?.filter(
      (k) => children.includes(k) || columnKeys.includes(k),
    )
    next[nodeKey] = { ...node, columnKeys, children, childOrder }
    return true
  }
  visit('root')
  return next
}

/**
 * Move the given categories' columns onto the ROOT node so they render at the
 * top (above the folders) — e.g. to surface a "Commonly Used" set. Pair with
 * `ColumnDrillDown`'s `rootColumnsFirst` prop.
 */
export function promoteCategoriesToRoot(
  built: ReturnType<typeof buildColumnTree>,
  categories: string[],
): ReturnType<typeof buildColumnTree> {
  const { tree, columnMap } = built
  const next = { ...tree }
  const rootCols: string[] = []
  const promotedFolders: string[] = []
  const removed = new Set<string>()
  for (const cat of categories) {
    const catKey = `cat::${cat}`
    const node = tree[catKey]
    if (!node) continue
    rootCols.push(...node.columnKeys)
    promotedFolders.push(...node.children)
    removed.add(catKey)
    delete next[catKey]
  }
  next.root = {
    ...tree.root,
    columnKeys: rootCols,
    children: [...promotedFolders, ...tree.root.children.filter((k) => !removed.has(k))],
    // The inherited `childOrder` from `tree.root` describes the PRE-promotion
    // children/columnKeys sets and would silently drop the newly-promoted
    // folders (never listed in it). Clear it so `orderedChildren` in
    // ColumnDrillDown falls back to folders-then-columns / columns-then-folders,
    // which matches this function's own (non-interleaved) promotion semantics.
    childOrder: undefined,
  }
  return { tree: next, columnMap }
}
